import { Components, Theme } from '@mui/material/styles';

const tableTheme: Components<Theme>['MuiTable'] = {
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      // Default styles (applied if size is not 'custom')
      ...(ownerState.size !== 'custom' && {
        '&.MuiTable-root': {
          '&.sx-small': {
            '& .MuiTableHead-root': {
              '& >.MuiTableRow-root': {
                '&:first-of-type .MuiTableCell-root': {
                  [theme.breakpoints.up('xs')]: {
                    padding: '3.2px',
                  },
                  [theme.breakpoints.up('sm')]: {
                    padding: '8px'
                  },
                  '& .MuiTypography-root': {
                    [theme.breakpoints.up('xs')]: {
                      fontSize: '0.85rem',
                      fontWeight: 'normal'
                    },
                    [theme.breakpoints.up('sm')]: {
                      fontSize: '18px',
                    },
                  },
                },
              }
            },
            '& .MuiTableRow-root .MuiTableCell-root': {
              [theme.breakpoints.up('xs')]: {
                fontSize: '0.85rem',
              },
              [theme.breakpoints.up('sm')]: {
                fontSize: '16px',
              },
            },
            '& .MuiTableRow-root.buttons-tbl-row .MuiTableCell-root, .MuiTableRow-root.surplus-tbl-row .MuiTableCell-root': {
              [theme.breakpoints.up('xs')]: {
                padding: '2px',
              },
              [theme.breakpoints.up('sm')]: {
                padding: '8px'
              },
              maxWidth: 'none',
              '&:first-of-type img': {
                [theme.breakpoints.up('xs')]: {
                  width: '27px'
                },
                [theme.breakpoints.up('sm')]: {
                  width: 'auto'
                },
              },
              ' .MuiTypography-root': {
                [theme.breakpoints.up('xs')]: {
                  fontSize: '0.85rem',
                },
                [theme.breakpoints.up('sm')]: {
                  fontSize: '16px',
                },
              },
            },
            '& .MuiTableFooter-root .MuiTableRow-root': {
              ' .MuiTableCell-root, .MuiTableCell-root .MuiTypography-root': {
                [theme.breakpoints.up('xs')]: {
                  fontSize: '0.85rem',
                },
                [theme.breakpoints.up('sm')]: {
                  fontSize: '18px',
                },
              },
            },
          },
          '& .MuiTableHead-root': {
            '& >.MuiTableRow-root': {
              '&:first-of-type .MuiTableCell-root': {
                background: theme.palette.warmBlue,
                '& .MuiTypography-root': {
                  fontSize: '18px',
                  fontWeight: 'bold',
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.neutralWhite,
                },
              },
              '&:nth-of-type(2) .MuiTableCell-root': {
                background: theme.palette.clearBlue,
                '& .MuiTypography-popup-message .MuiTypography-root': {
                  color: 'black',
                  fontWeight: 'normal'
                },
                '& .MuiTypography-root': {
                  fontSize: '16px',
                  fontWeight: 'bold',
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.neutralWhite,
                },
              },
              '.MuiTableCell-root': {
                color: theme.palette.corporateBlue,
                fontWeight: 'bold',
                fontFamily: theme.typography.fontFamily,
              },
            },
          },
          '& .MuiTableRow-root .MuiTableCell-root, .MuiTableRow-root .MuiTableCell-root .MuiTypography-root': {
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.corporateBlue,
            fontSize: '16px',
            '&.red': {
              color: theme.palette.red.A700,
            },
            '&.white': {
              color: theme.palette.neutralWhite,
            }
          },
          '& .MuiTableFooter-root .MuiTableRow-root': {
            ' .MuiTableCell-root, .MuiTableCell-root .MuiTypography-root': {
              background: theme.palette.oceanicGreen,
              fontSize: '16px',
              fontWeight: 'bold',
              fontFamily: theme.typography.fontFamily,
              color: theme.palette.corporateBlue,
            },
          },
          '& .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root.no-data': {
            color: theme.palette.grey.A200
          }
        },
      }),

      // Three-level specific styles
      '&.three-level': {
        '& > .MuiTableBody-root > .MuiTableRow-root': {
          background: theme.palette.warmBlue,
          '& > .MuiTableCell-root': {
            borderBottom: `1px solid ${theme.palette.neutralWhite}`,
            color: theme.palette.neutralWhite,
            '& .MuiTableRow-root': {
              color: theme.palette.black,
            },
            '& .MuiTypography-root.MuiTypography-body1.MuiTypography-gutterBottom' : {
              color: theme.palette.common.white,
              font: '18px',
              '& .MuiBox-root': {
                '& .MuiTypography-root.MuiTypography-body1:first-of-type': {
                  color: theme.palette.common.white,
                  font: '18px',
                },
              },
            }
          },
        },
      },

      // Two-level specific styles
      '&.two-level': {
        '& > .MuiTableHead-root > .MuiTableRow-root': {
          background: theme.palette.warmBlue,
          borderBottom: `1px solid ${theme.palette.neutralWhite}`,
          color: theme.palette.neutralWhite,
        },
        ' > .MuiTableHead-root .MuiTableCell-root .MuiTypography-root': {
          fontWeight: 'normal',
        },
      },

      // Common styles for one-level, two-level, and three-level
      '&.three-level, &.two-level, &.one-level': {
        border: `1px solid ${theme.palette.coldWhite}`,
        '& .MuiTypography-root': {
          fontFamily: theme.typography.fontFamily,
          fontWeight: 'normal'
        },
        ' .MuiTableHead-root .MuiTypography-root': {
          fontSize: '18px',
          fontWeight: 'bold',
        },
      },

      // Info-box specific styles
      '&.info-box': {
        '& .MuiTableHead-root .MuiTableCell-root': {
          background: theme.palette.warmBlue,
          color: theme.palette.neutralWhite,
          textAlign: 'center',
          ' .MuiTypography-root': {
            fontFamily: theme.typography.fontFamily,
            fontSize: '18px',
            fontWeight: 'bold',
          },
          '& .subheader': {
              fontSize: '16px',
          },
          '& .pending': {
            color: theme.palette.orange,
          },
          '& .progress': {
            color: theme.palette.oceanicGreen,
          },
        },
        '& .MuiTableBody-root': {
          background: theme.palette.blue[200],
          '& .MuiTableCell-root': {
            fontSize: '16px',
            textAlign: 'center',
          },
        },
      },

      '&.availabilityTable': {
        '& .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root.hasDarkBackground': {
          color: theme.palette.common.white
        },
      },
    }),
  },
};

export default tableTheme;
